import {
  DEFAULT_CSS_SELECTOR_ALLOWLIST,
  type BeforePlugin,
  type ElementInteractionsOptions,
  type EnrichmentPlugin,
  type Event as AmplitudeEvent,
} from '@amplitude/analytics-browser/lib/esm/types';

import {
  AMPLITUDE_PRODUCT_AREA_ALLOWLISTS,
  getAmplitudePageUrlAllowlistForProductAreaIds,
} from 'analytics/amplitudePageUrlAllowlist';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import APIUtil from 'util/APIUtil';
import pick from 'util/pick';

function getAllApiIdPrefixes(): ReadonlyArray<string> {
  const prefixes = new Set(Object.keys(getPrefixToTypename()));
  for (const type of Object.values(ModelTypeConstants)) {
    const prefix = APIUtil.typeToApiPrefix(type);
    if (prefix) {
      prefixes.add(prefix);
    }
  }
  // Longest first so shorter prefixes cannot partially match longer ones.
  return [...prefixes].sort((a, b) => b.length - a.length);
}

/**
 * Opaque API ID is prefix_ + alphanumeric token. Optional `-vanity` name slug (from object name)
 * is customer data and must be replaced with a fixed mask while keeping the opaque ID.
 */
function buildBenchlingIdWithOptionalVanityPatternString(prefixes: ReadonlyArray<string>): string {
  return `\\b(${prefixes.join('|')})_([A-Za-z0-9]+)(-[A-Za-z0-9_-]+)?\\b`;
}

const BENCHLING_ID_WITH_OPTIONAL_VANITY_REGEX = new RegExp(
  buildBenchlingIdWithOptionalVanityPatternString(getAllApiIdPrefixes()),
  'g'
);

const SANITIZED_URL_PROPERTY_KEYS = [
  '[Amplitude] Page URL',
  '[Amplitude] Page Path',
  '[Amplitude] Page Location',
  '[Amplitude] Previous Page Location',
  'Page Path',
  'Page Location',
  '[Amplitude] Element Href',
  'page_path',
  'page_url',
  'page_location',
  'referrer',
];

/**
 * Campaign / attribution keys Amplitude attaches to page-view events (`BASE_CAMPAIGN`).
 * `referrer` is listed with SANITIZED_URL_PROPERTY_KEYS instead so its value is URL-sanitized.
 */
const PAGE_VIEW_CAMPAIGN_PROPERTY_KEYS = [
  'utm_campaign',
  'utm_content',
  'utm_id',
  'utm_medium',
  'utm_source',
  'utm_term',
  'referring_domain',
  'dclid',
  'gbraid',
  'gclid',
  'fbclid',
  'ko_click_id',
  'li_fat_id',
  'msclkid',
  'rdt_cid',
  'ttclid',
  'twclid',
  'wbraid',
];

/**
 * Amplitude stores matching data-test-* attrs under this property, with the
 * `data-test-` prefix stripped from each key (see DataTestAttrs field names).
 */
const ELEMENT_ATTRIBUTES_PROPERTY_KEY = '[Amplitude] Element Attributes';

/** Keep stable UI selectors; drop `index`, `key`, and ad-hoc attrs (often customer text/IDs/timestamps). */
const ALLOWED_ELEMENT_ATTRIBUTE_KEYS = ['component', 'element'];

/** Extra Element Attributes keys sent when the target node has `data-amp-safe`. */
const AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS = ['key', 'index'];

/**
 * Put this on an element whose `data-test-key` / `data-test-index` values are
 * not customer data / PII. The privacy plugin then sends those in addition to
 * `component` and `element`.
 *
 * Not an Amplitude SDK flag — only this plugin reads it. Autocapture does not
 * copy it into `[Amplitude] Element Attributes` (those are `data-test-*` only);
 * the plugin looks for it on the target node in `[Amplitude] Element Hierarchy`
 * and then drops it from the payload.
 *
 * @example
 * <button {...getTestProps({component: 'Save', element: 'root', key: 'status'})} data-amp-safe={true} />
 */
export const AMP_SAFE_ATTR = 'data-amp-safe';

/** DOM ancestry of the interacted element, from the element itself up to (excluding) `<html>`. */
const ELEMENT_HIERARCHY_PROPERTY_KEY = '[Amplitude] Element Hierarchy';

/**
 * Structural keys kept on each hierarchy node. `id` and `classes` are omitted, matching
 * the dropped `[Amplitude] Element ID` / `Class` / `Path` event properties.
 */
const ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS = ['tag', 'index', 'indexOfType', 'prevSib'];

/**
 * Hierarchy `attrs` keys are full attribute names, unlike the prefix-stripped
 * ELEMENT_ATTRIBUTES_PROPERTY_KEY map. Reuse the same data-test-* allowlist, plus `type`.
 * Every other attr (title, placeholder, aria-*, ad-hoc data-*) can carry customer text.
 * `data-amp-safe` is read then dropped; it is never sent.
 */
const ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS = [
  'type',
  ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS.map((key) => `data-test-${key}`),
];

const AMP_SAFE_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS = AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS.map(
  (key) => `data-test-${key}`
);

/**
 * Known Amplitude autocapture / page-view keys we keep. New keys are omitted until
 * they are added here. Custom precision-tracking properties are not sent via this SDK.
 */
const ALLOWED_EVENT_PROPERTY_KEYS = [
  ...SANITIZED_URL_PROPERTY_KEYS,
  ...PAGE_VIEW_CAMPAIGN_PROPERTY_KEYS,
  ELEMENT_ATTRIBUTES_PROPERTY_KEY,
  ELEMENT_HIERARCHY_PROPERTY_KEY,
  '[Amplitude] Page Domain',
  '[Amplitude] Previous Page Type',
  '[Amplitude] Page Counter',
  '[Amplitude] Page View ID',
  '[Amplitude] Element Tag',
  '[Amplitude] Element Position Left',
  '[Amplitude] Element Position Top',
  '[Amplitude] Viewport Height',
  '[Amplitude] Viewport Width',
];

const MASKED_TEXT = '*****';

export function getAmplitudeElementInteractionsOptions(): ElementInteractionsOptions {
  const options: ElementInteractionsOptions = {
    // Capture data-test-* attrs; privacy plugin keeps component/element, and also
    // key/index when `data-amp-safe` is set on the element.
    dataAttributePrefix: 'data-test-',
    visualTaggingOptions: {
      enabled: true,
    },
    viewportContentUpdated: {
      enabled: false,
    },
    cssSelectorAllowlist: ['[tabindex="0"]', ...DEFAULT_CSS_SELECTOR_ALLOWLIST],
  };
  if (!BenchlingConfig.ANALYTICS_DISABLE_AUTOCAPTURE_PAGE_URL_ALLOWLIST) {
    options.pageUrlAllowlist = getAmplitudePageUrlAllowlistForProductAreaIds(
      BenchlingConfig.ANALYTICS_AUTOCAPTURE_PAGE_URL_ALLOWLIST_PRODUCT_AREAS,
      AMPLITUDE_PRODUCT_AREA_ALLOWLISTS
    );
  }
  return options;
}

function sanitizeBenchlingIds(value: string): string {
  return value.replace(
    BENCHLING_ID_WITH_OPTIONAL_VANITY_REGEX,
    (_match, prefix: string, opaqueId: string, vanitySuffix: string | undefined) =>
      vanitySuffix ? `${prefix}_${opaqueId}-${MASKED_TEXT}` : `${prefix}_${opaqueId}`
  );
}

/**
 * Sanitize URLs before events leave the browser: strip query/hash, keep opaque Benchling API IDs,
 * and replace name-derived vanity suffixes with a fixed mask. Path-absolute inputs (leading `/`)
 * stay path-shaped; other non-URL strings only get ID sanitization.
 */
export function sanitizeAmplitudeUrl(url: string): string {
  if (!/^https?:\/\//.test(url) && !url.startsWith('/')) {
    return sanitizeBenchlingIds(url);
  }

  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://benchling.com';
    const parsed = new URL(url, baseOrigin);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = parsed.pathname
      .split('/')
      .map((segment) => sanitizeBenchlingIds(segment))
      .join('/');
    if (url.startsWith('/')) {
      return parsed.pathname;
    }
    return parsed.toString();
  } catch (error) {
    if (error instanceof TypeError) {
      return sanitizeBenchlingIds(url);
    }
    throw error;
  }
}

/** React `data-amp-safe={true}` stringifies to `"true"` in the DOM. */
function isAmpSafe(attrs: unknown): boolean {
  if (attrs == null || typeof attrs !== 'object') {
    return false;
  }
  return (attrs as Record<string, unknown>)[AMP_SAFE_ATTR] === 'true';
}

function filterElementAttributes(sanitized: Record<string, unknown>): void {
  const attributes = sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
  if (attributes == null) {
    return;
  }
  const hierarchy = sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY];
  let extraKeys: Array<string> = [];
  if (hierarchy != null) {
    const target = (hierarchy as Array<Record<string, unknown>>)[0];
    if (isAmpSafe(target?.attrs)) {
      extraKeys = AMP_SAFE_ELEMENT_ATTRIBUTE_KEYS;
    }
  }
  const filtered = pick(
    attributes as Record<string, unknown>,
    ...ALLOWED_ELEMENT_ATTRIBUTE_KEYS,
    ...extraKeys
  );
  if (Object.keys(filtered).length === 0) {
    delete sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY];
    return;
  }
  sanitized[ELEMENT_ATTRIBUTES_PROPERTY_KEY] = filtered;
}

function filterElementHierarchyNode(node: Record<string, unknown>): Record<string, unknown> {
  const filtered = pick(node, ...ALLOWED_ELEMENT_HIERARCHY_NODE_KEYS);
  if (node.attrs != null) {
    const attrs = node.attrs as Record<string, unknown>;
    const filteredAttrs = pick(
      attrs,
      ...ALLOWED_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS,
      ...(isAmpSafe(attrs) ? AMP_SAFE_ELEMENT_HIERARCHY_ATTRIBUTE_KEYS : [])
    );
    if (Object.keys(filteredAttrs).length > 0) {
      filtered.attrs = filteredAttrs;
    }
  }
  return filtered;
}

function filterElementHierarchy(sanitized: Record<string, unknown>): void {
  const hierarchy = sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY];
  if (hierarchy == null) {
    return;
  }
  sanitized[ELEMENT_HIERARCHY_PROPERTY_KEY] = (hierarchy as Array<Record<string, unknown>>).map((node) =>
    filterElementHierarchyNode(node)
  );
}

function sanitizeUrlProperties(properties: Record<string, unknown>): void {
  for (const key of SANITIZED_URL_PROPERTY_KEYS) {
    if (key in properties && typeof properties[key] === 'string') {
      properties[key] = sanitizeAmplitudeUrl(properties[key]);
    }
  }
}

function sanitizeProperties(properties: Record<string, unknown>): Record<string, unknown> {
  let sanitized: Record<string, unknown>;
  if (BenchlingConfig.ANALYTICS_DISABLE_AUTOCAPTURE_PROPERTY_ALLOWLIST) {
    sanitized = {...properties};
  } else {
    sanitized = pick(properties, ...ALLOWED_EVENT_PROPERTY_KEYS);
    filterElementAttributes(sanitized);
    filterElementHierarchy(sanitized);
  }
  sanitizeUrlProperties(sanitized);
  return sanitized;
}

export interface AmplitudePrivacyReadyLatch {
  waitUntilReady: () => Promise<void>;
  markReady: () => void;
}

export function createAmplitudePrivacyReadyLatch(): AmplitudePrivacyReadyLatch {
  let ready = false;
  let resolvers: Array<() => void> = [];
  return {
    waitUntilReady: (): Promise<void> => {
      if (ready) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        resolvers.push(resolve);
      });
    },
    markReady: (): void => {
      ready = true;
      for (const resolve of resolvers) {
        resolve();
      }
      resolvers = [];
    },
  };
}

/**
 * Before-phase plugin that holds events until the privacy enrichment plugin is installed.
 * Performs no redaction — enrichment owns all sanitization.
 */
export function createAmplitudePrivacyBeforePlugin(waitUntilReady: () => Promise<void>): BeforePlugin {
  return {
    name: 'benchling-amplitude-privacy-before',
    type: 'before',
    execute: async (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      await waitUntilReady();
      return event;
    },
  };
}

/** Enrichment plugin that allowlists autocapture properties and always sanitizes URLs. */
export function createAmplitudePrivacyEnrichmentPlugin(): EnrichmentPlugin {
  return {
    name: 'benchling-amplitude-privacy',
    type: 'enrichment',
    execute: (event: AmplitudeEvent): Promise<AmplitudeEvent | null> => {
      // Identify / setGroup use user_properties and groups, not event_properties.
      if (event.event_properties == null) {
        return Promise.resolve(event);
      }
      // Event includes RevenueEvent, whose event_properties aren't freely string-indexable.
      return Promise.resolve({
        ...event,
        event_properties: sanitizeProperties(event.event_properties as Record<string, unknown>),
      });
    },
  };
}
